import React, { useMemo } from 'react';
import { ColumnDef, TableMeta } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import ExcelJS from 'exceljs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { Row, Col } from 'react-bootstrap';
import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { CallData } from './CallResultsData';

interface CallResultsTableProps {
  data: CallData[];
}

export const callResultsDataTableColumns = (
  // eslint-disable-next-line
  data: CallData[]
): ColumnDef<CallData>[] => {
  const columns: ColumnDef<CallData>[] = [
    {
      accessorKey: 'calldatetime',
      header: 'Call date',
      cell: ({ row: { original } }) =>
        original.calldatetime
          ? new Date(original.calldatetime).toLocaleString()
          : '-',
      meta: {
        headerProps: { style: { width: '8%' }, className: 'pe-2 ps-0 fs-10' },
        cellProps: { className: 'py-0 pe-2 ps-0 white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'callid',
      header: 'callid',
      meta: {
        headerProps: { style: { width: '7%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'accountref',
      header: 'accountref',
      meta: {
        headerProps: { style: { width: '7%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'exp_id',
      header: 'Exp ID',
      meta: {
        headerProps: { style: { width: '7%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'customerref',
      header: 'Customer',
      meta: {
        headerProps: { style: { width: '7%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'customerfullname',
      header: 'Customer Name',
      meta: {
        headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'phonenumber',
      header: 'Phone',
      meta: {
        headerProps: { style: { width: '7%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'calltype',
      header: 'Type',
      meta: {
        headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'duration',
      header: 'Duration',
      cell: ({ row: { original } }) => {
        const value = original.duration;
        if (typeof value !== 'number') return '-';
        return value;
      },
      meta: {
        headerProps: {
          style: { width: '6%' },
          className: 'px-1 fs-10 text-center'
        },
        cellProps: { className: 'px-1 text-center white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'phoneresult',
      header: 'Result',
      cell: ({ row: { original } }) => {
        const value = original.phoneresult;
        return value === 'ANSWERED' ? (
          <span className="text-success">{value}</span>
        ) : (
          <span>{value}</span>
        );
      },
      meta: {
        headerProps: { style: { width: '6%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'transferenciatpv',
      header: 'TPV trnf.',
      cell: ({ row: { original } }) => {
        const value = original.transferenciatpv;
        return value === 1 ? (
          <FontAwesomeIcon icon={faCheck} className="text-success" />
        ) : (
          <FontAwesomeIcon icon={faTimes} className="text-danger" />
        );
      },
      meta: {
        headerProps: {
          style: { width: '5%' },
          className: 'px-1 fs-10 text-center'
        },
        cellProps: { className: 'px-1 text-center white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'transferenciagestor',
      header: 'Agent trnf.',
      cell: ({ row: { original } }) => {
        const value = original.transferenciagestor;
        return value === 1 ? (
          <FontAwesomeIcon icon={faCheck} className="text-success" />
        ) : (
          <FontAwesomeIcon icon={faTimes} className="text-danger" />
        );
      },
      meta: {
        headerProps: {
          style: { width: '5%' },
          className: 'px-1 fs-10 text-center'
        },
        cellProps: { className: 'px-1 text-center white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'identificadaia',
      header: 'AI rev.',
      cell: ({ row: { original } }) => {
        const value = original.identificadaia;
        return value === 1 ? (
          <FontAwesomeIcon icon={faCheck} className="text-success" />
        ) : (
          <FontAwesomeIcon icon={faTimes} className="text-danger" />
        );
      },
      meta: {
        headerProps: {
          style: { width: '5%' },
          className: 'px-1 fs-10 text-center'
        },
        cellProps: { className: 'px-1 text-center white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'rpc',
      header: 'RPC',
      cell: ({ row: { original } }) => {
        const value = original.rpc;
        return value === 1 ? (
          <FontAwesomeIcon icon={faCheck} className="text-success" />
        ) : (
          <FontAwesomeIcon icon={faTimes} className="text-danger" />
        );
      },
      meta: {
        headerProps: {
          style: { width: '5%' },
          className: 'px-1 fs-10 text-center'
        },
        cellProps: { className: 'px-1 text-center fs-10' }
      }
    },
    {
      accessorKey: 'resultado_final',
      header: 'Call Result',
      meta: {
        headerProps: { style: { width: '8%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'desglose_final',
      header: 'Result detail',
      meta: {
        headerProps: { style: { width: '8%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap fs-10' }
      }
    },
    {
      accessorKey: 'resultadopositivo',
      header: 'Success',
      cell: ({ row: { original } }) => {
        const value = original.resultadopositivo;
        return value === 1 ? (
          <FontAwesomeIcon icon={faCheck} className="text-success" />
        ) : (
          <FontAwesomeIcon icon={faTimes} className="text-danger" />
        );
      },
      meta: {
        headerProps: {
          style: { width: '5%' },
          className: 'px-1 fs-10 text-center'
        },
        cellProps: { className: 'px-1 text-center fs-10' }
      }
    }
  ];

  return columns;
};

export const HVACallResultsDataTable = () => {
  const table = useAdvanceTableContext();
  const data = table.options.data as CallData[];

  const tableOptions = useMemo(
    () => ({
      meta: {} as TableMeta<CallData>
    }),
    []
  );

  const handleExportToExcel = async () => {
    // Crear un nuevo workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Call Results');

    // Definir las columnas
    const exportColumns = [
      { field: 'calldatetime', header: 'Call date', width: 18 },
      { field: 'callid', header: 'callid', width: 15 },
      { field: 'accountref', header: 'Account', width: 15 },
      { field: 'exp_id', header: 'Exp_id', width: 15 },
      { field: 'customerref', header: 'Customer Ref.', width: 15 },
      { field: 'customerfullname', header: 'Customer Name', width: 20 },
      { field: 'phonenumber', header: 'Phone', width: 15 },
      { field: 'calltype', header: 'Type', width: 10 },
      { field: 'duration', header: 'Duration', width: 10 },
      { field: 'phoneresult', header: 'Result', width: 12 },
      { field: 'transferenciatpv', header: 'TPV transfer', width: 12 },
      { field: 'transferenciagestor', header: 'Agent transfer', width: 12 },
      { field: 'identificadaia', header: 'AI revealed', width: 12 },
      { field: 'rpc', header: 'rpc', width: 10 },
      { field: 'resultado_final', header: 'Call Result', width: 15 },
      { field: 'desglose_final', header: 'Result detail', width: 20 },
      { field: 'resultadopositivo', header: 'Success', width: 10 }
    ];

    // Configurar columnas
    worksheet.columns = exportColumns.map(col => ({
      header: col.header,
      key: col.field,
      width: col.width
    }));

    // Agregar los datos
    data.forEach(row => {
      const rowData: Record<string, string | number> = {};

      exportColumns.forEach(column => {
        const field = column.field as keyof CallData;

        // Manejar transformaciones especiales
        if (field === 'calldatetime') {
          rowData[field] = row[field]
            ? new Date(row[field] as string).toLocaleString()
            : '-';
        } else if (
          [
            'transferenciatpv',
            'transferenciagestor',
            'identificadaia',
            'rpc',
            'resultadopositivo'
          ].includes(field as string)
        ) {
          const value = row[field] as number | undefined;
          rowData[field] = value === 1 ? 'Yes' : 'No';
        } else {
          const value = row[field];
          rowData[field] = value !== undefined ? String(value) : '-';
        }
      });

      worksheet.addRow(rowData);
    });

    // Estilizar el encabezado
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    // Generar el archivo y descargarlo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    // Crear enlace de descarga
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `call_results_${
      new Date().toISOString().split('T')[0]
    }.xlsx`;
    link.click();

    // Limpiar
    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div>
        <Row className="d-flex justify-content-between align-items-center">
          <Col>
            <h5>Raw Data</h5>
          </Col>
          <Col className="d-flex justify-content-end">
            <Button
              className="pt-0 pb-0"
              variant="link"
              onClick={handleExportToExcel}
              disabled={!data.length}
            >
              <FontAwesomeIcon icon={faDownload} className="me-2 fs-9" />
              Download to Excel
            </Button>
          </Col>
        </Row>
      </div>
      <AdvanceTable
        tableProps={{
          size: 'sm',
          className: 'phoenix-table fs-9',
          ...tableOptions
        }}
        rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
      />
      <AdvanceTableFooter pagination />
    </div>
  );
};

const CallResultsTable: React.FC<CallResultsTableProps> = ({ data }) => {
  const columns = useMemo(() => callResultsDataTableColumns(data), [data]);

  const table = useAdvanceTable({
    data,
    columns,
    pageSize: 15,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <div>
      <div className="mt-0 bg-body-emphasis pb-3">
        <AdvanceTableProvider {...table}>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 border-translucent position-relative top-1">
            <HVACallResultsDataTable />
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default CallResultsTable;
